import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import {
	Grid,
	TextField,
	IconButton,
	Paper,
	MenuItem as MuiMenuItem,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Menu,
	Divider,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import {
	AddCircle,
	CropFree as CropFreeIcon,
	Mic as MicIcon,
	MoreVert,
} from '@material-ui/icons';
import AppContext from 'containers/App/Store/AppContext';
import {
	useGetOutletQuery,
	useGetMenuItemQuery,
	useGetUomQuery,
	useGetReasonQuery,
	useGetStockBalanceQuery,
	useCreateStockTransactionMutation,
} from 'generated/graphql';
import React, { useContext, useEffect, useReducer } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import { SystemMsgs } from 'helpers/SystemMsg';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents';
import { DynamicFooter } from 'components/Footer/DynamicFooter';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import NumberFormat from 'react-number-format';
import { useBarcodeScanner } from 'helpers/hooks/useBarcodeScanner';
import { isBefore, startOfDay } from 'date-fns';

type StockTransactionType = 'STOCK_IN' | 'STOCK_OUT' | 'STOCK_ADJUSTMENT' | '';

interface StockTransactionItem {
	ID?: string;
	menuItemID: string;
	menuItemName?: string;
	uomID: string;
	uomName?: string;
	qtyReceived?: number;
	transferQty?: number;
	qtyAdjusted?: number;
	reasonID?: string;
	reasonName?: string;
	remark: string;
}

interface StockTransactionFormState {
	// Form fields
	transactionType: StockTransactionType;
	trxDate: Date | null;
	docRef: string;
	docNo: string;
	toWarehouseID: string;
	description: string;
	items: StockTransactionItem[];
	// Dialog state
	openDialog: boolean;
	selectedMenuItemID: string;
	selectedUomID: string;
	qty: number;
	balanceQty: number;
	reasonID: string;
	remark: string;
	// Menu state
	anchorEl: HTMLElement | null;
	selectedItemIndex: number | null;
	// Barcode scanner state
	isBarcodeScanningEnabled: boolean;
	menuItemError: string;
}

type StockTransactionFormAction =
	| { type: 'SET_TRANSACTION_TYPE'; payload: StockTransactionType }
	| { type: 'SET_TRX_DATE'; payload: Date | null }
	| { type: 'SET_DOC_REF'; payload: string }
	| { type: 'SET_DOC_NO'; payload: string }
	| { type: 'SET_TO_WAREHOUSE_ID'; payload: string }
	| { type: 'SET_DESCRIPTION'; payload: string }
	| { type: 'SET_ITEMS'; payload: StockTransactionItem[] }
	| { type: 'ADD_ITEM'; payload: StockTransactionItem }
	| { type: 'REMOVE_ITEM'; payload: number }
	| { type: 'SET_OPEN_DIALOG'; payload: boolean }
	| { type: 'SET_SELECTED_MENU_ITEM_ID'; payload: string }
	| { type: 'SET_SELECTED_UOM_ID'; payload: string }
	| { type: 'SET_QTY'; payload: number }
	| { type: 'SET_BALANCE_QTY'; payload: number }
	| { type: 'SET_REASON_ID'; payload: string }
	| { type: 'SET_REMARK'; payload: string }
	| { type: 'RESET_DIALOG_FORM' }
	| { type: 'SET_ANCHOR_EL'; payload: HTMLElement | null }
	| { type: 'SET_SELECTED_ITEM_INDEX'; payload: number | null }
	| {
			type: 'SET_EDIT_ITEM';
			payload: { item: StockTransactionItem; index: number };
	  }
	| { type: 'SET_BARCODE_SCANNING_ENABLED'; payload: boolean }
	| { type: 'SET_MENU_ITEM_ERROR'; payload: string };

const initialState: StockTransactionFormState = {
	transactionType: '',
	trxDate: new Date(),
	docRef: '',
	docNo: '',
	toWarehouseID: '',
	description: '',
	items: [],
	openDialog: false,
	selectedMenuItemID: '',
	selectedUomID: '',
	qty: 0,
	balanceQty: 0,
	reasonID: '',
	remark: '',
	anchorEl: null,
	selectedItemIndex: null,
	isBarcodeScanningEnabled: false,
	menuItemError: '',
};

const stockTransactionFormReducer = (
	state: StockTransactionFormState,
	action: StockTransactionFormAction,
): StockTransactionFormState => {
	switch (action.type) {
		case 'SET_TRANSACTION_TYPE':
			return {
				...state,
				transactionType: action.payload,
				docNo: '', // Reset docNo when changing transaction type
				toWarehouseID: '', // Reset toWarehouseID when changing transaction type
			};
		case 'SET_TRX_DATE':
			return { ...state, trxDate: action.payload };
		case 'SET_DOC_REF':
			return { ...state, docRef: action.payload };
		case 'SET_DOC_NO':
			return { ...state, docNo: action.payload };
		case 'SET_TO_WAREHOUSE_ID':
			return { ...state, toWarehouseID: action.payload };
		case 'SET_DESCRIPTION':
			return { ...state, description: action.payload };
		case 'SET_ITEMS':
			return { ...state, items: action.payload };
		case 'ADD_ITEM':
			return { ...state, items: [...state.items, action.payload] };
		case 'REMOVE_ITEM':
			return {
				...state,
				items: state.items.filter((_, i) => i !== action.payload),
			};
		case 'SET_OPEN_DIALOG':
			return { ...state, openDialog: action.payload };
		case 'SET_SELECTED_MENU_ITEM_ID':
			return {
				...state,
				selectedMenuItemID: action.payload,
				selectedUomID: '',
				balanceQty: 0,
				qty: 0,
			};
		case 'SET_SELECTED_UOM_ID':
			return { ...state, selectedUomID: action.payload };
		case 'SET_QTY':
			return { ...state, qty: action.payload };
		case 'SET_BALANCE_QTY':
			return { ...state, balanceQty: action.payload };
		case 'SET_REASON_ID':
			return { ...state, reasonID: action.payload };
		case 'SET_REMARK':
			return { ...state, remark: action.payload };
		case 'RESET_DIALOG_FORM':
			return {
				...state,
				selectedMenuItemID: '',
				selectedUomID: '',
				qty: 0,
				balanceQty: 0,
				reasonID: '',
				remark: '',
				selectedItemIndex: null,
				isBarcodeScanningEnabled: false,
			};
		case 'SET_BARCODE_SCANNING_ENABLED':
			return { ...state, isBarcodeScanningEnabled: action.payload };
		case 'SET_ANCHOR_EL':
			return { ...state, anchorEl: action.payload };
		case 'SET_SELECTED_ITEM_INDEX':
			return { ...state, selectedItemIndex: action.payload };
		case 'SET_EDIT_ITEM':
			const editItem = action.payload.item;
			return {
				...state,
				selectedMenuItemID: editItem.menuItemID,
				selectedUomID: editItem.uomID,
				qty: getItemQty(editItem, state.transactionType),
				reasonID: editItem.reasonID || '',
				remark: editItem.remark,
				openDialog: true,
				selectedItemIndex: action.payload.index,
				anchorEl: null,
				isBarcodeScanningEnabled: true,
				menuItemError: '',
			};
		case 'SET_MENU_ITEM_ERROR':
			return {
				...state,
				menuItemError: action.payload,
			};
		default:
			return state;
	}
};

const getItemQty = (
	item: StockTransactionItem,
	transactionType: StockTransactionType,
): number => {
	if (transactionType === 'STOCK_IN') return item.qtyReceived || 0;
	if (transactionType === 'STOCK_OUT') return item.transferQty || 0;
	if (transactionType === 'STOCK_ADJUSTMENT') return item.qtyAdjusted || 0;
	return 0;
};

export const StockTransactionForm: React.FC = () => {
	const { outletID, transactionID }: any = useParams();
	const { globalState, dispatch }: any = useContext(AppContext as any);
	const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
		SnackBarContext as any,
	);
	const history = useHistory();

	const [state, dispatchState] = useReducer(
		stockTransactionFormReducer,
		initialState,
	);

	const [
		createStockTransaction,
		{ loading: createLoading },
	] = useCreateStockTransactionMutation({
		refetchQueries: ['getStockTransactions', 'getStockBalance'],
		onCompleted: () => {
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.createNewRecord());
			setTimeout(() => {
				history.push(`/menu/outlet-app/${outletID}/stock-monitoring?tab=1`);
			}, 1000);
		},
		onError: error => {
			const errorMsg =
				error.message || 'Failed to create Stock Transaction';
			setOpenSnackBar(true);
			setSnackBarMsg(error.message || 'Failed to create Stock Transaction');
			dispatchState({
				type: 'SET_MENU_ITEM_ERROR',
				payload: errorMsg,
			});
		},
	});

	const {
		called: outletCalled,
		loading: outletLoading,
		data: { getOutlet } = { getOutlet: [] },
	} = useGetOutletQuery({
		fetchPolicy: 'network-only',
		variables: { ID: outletID },
	});

	// Fetch all outlets for warehouse selection (for STOCK_OUT)
	const { data: outletsData } = useGetOutletQuery({
		fetchPolicy: 'network-only',
	});

	// Filter out current outlet from warehouses list
	const warehouses = (outletsData?.getOutlet || []).filter(
		(outlet: any) => outlet.ID !== outletID && outlet.isRetail,
	);

	const {
		loading: menuItemLoading,
		data: { getMenuItem } = { getMenuItem: [] },
	} = useGetMenuItemQuery({
		variables: {
			outletID: outletID,
			isActive: true,
			isSetMenu: false,
			isInventory: true,
			orderByAsc: 'name',
		},
		fetchPolicy: 'network-only',
	});

	const {
	data: warehouseMenuItemData,
	loading: warehouseMenuItemLoading,
	} = useGetMenuItemQuery({
	variables: {
		outletID: state.toWarehouseID,
		isActive: true,
		isInventory: true,
		isSetMenu: false,
	},
	fetchPolicy: 'network-only',
	skip: !state.toWarehouseID || state.transactionType !== 'STOCK_OUT',
	});

	const {
		loading: uomLoading,
		data: { getUOM } = { getUOM: [] },
	} = useGetUomQuery({
		fetchPolicy: 'network-only',
	});

	const {
		loading: reasonLoading,
		data: { getReason } = { getReason: [] },
	} = useGetReasonQuery({
		variables: {
			outletID: outletID,
			orderByAsc: 'name',
		},
		fetchPolicy: 'network-only',
	});

	// Fetch stock balance when menu item is selected (for STOCK_OUT validation)
	const {
		data: stockBalanceData,
		loading: stockBalanceLoading,
	} = useGetStockBalanceQuery({
		variables: {
			outletID: outletID,
			menuItemID: state.selectedMenuItemID || '',
		},
		fetchPolicy: 'network-only',
		skip: !state.selectedMenuItemID || state.transactionType !== 'STOCK_OUT',
	});

	// Update balance quantity when stock balance data is loaded
	useEffect(() => {
		if (
			stockBalanceData?.getStockBalance &&
			stockBalanceData.getStockBalance.length > 0 &&
			state.transactionType === 'STOCK_OUT'
		) {
			const stockBalance = stockBalanceData.getStockBalance[0];
			const balanceQty =
				parseFloat(String(stockBalance.stockBalanceQty || 0)) || 0;
			dispatchState({ type: 'SET_BALANCE_QTY', payload: balanceQty });		
		} 
	}, [stockBalanceData, state.selectedMenuItemID, state.transactionType]);
	useEffect(() => {
		if (state.transactionType !== 'STOCK_OUT' || !state.selectedMenuItemID) {
			return;
		}
	
		if (!state.toWarehouseID) {
			dispatchState({
				type: 'SET_MENU_ITEM_ERROR',
				payload: 'Please select a warehouse first.',
			});
			return;
		}

	const warehouseItems = warehouseMenuItemData?.getMenuItem || [];

	const selectedItem = getMenuItem?.find(
		(i: any) => i.ID === state.selectedMenuItemID,
	);

	const selectedName = selectedItem?.name?.trim().toLowerCase();

	const exists = warehouseItems.some((i: any) =>
		(i.name || '').trim().toLowerCase() === selectedName,
	);

	if (!exists) {
		dispatchState({
		type: 'SET_MENU_ITEM_ERROR',
		payload: 'Selected stock item not exist in the selected warehouse.',
		});
	} else {
		dispatchState({
		type: 'SET_MENU_ITEM_ERROR',
		payload: '',
		});
	}
	}, [
	state.selectedMenuItemID,
	state.toWarehouseID,
	state.transactionType,
	warehouseMenuItemData,
	getMenuItem,
	]);
	// Barcode scanner hook
	const handleBarcodeScan = (barcode: string) => {
		if (!state.openDialog || !state.isBarcodeScanningEnabled) return;

		const matchingMenuItem = getMenuItem?.find(
			(item: any) =>
				item.barCode &&
				item.barCode.trim().toLowerCase() === barcode.trim().toLowerCase(),
		);

		if (matchingMenuItem) {
			dispatchState({
				type: 'SET_SELECTED_MENU_ITEM_ID',
				payload: matchingMenuItem.ID,
			});
			if (matchingMenuItem.stockUomID) {
				dispatchState({
					type: 'SET_SELECTED_UOM_ID',
					payload: matchingMenuItem.stockUomID,
				});
			}
			setOpenSnackBar(true);
			setSnackBarMsg(`Scanned: ${matchingMenuItem.name}`);
		} else {
			setOpenSnackBar(true);
			setSnackBarMsg(`No menu item found with barcode: ${barcode}`);
		}
	};

	const { isScanning } = useBarcodeScanner(
		handleBarcodeScan,
		state.openDialog && state.isBarcodeScanningEnabled,
	);

	// Auto-focus barcode input when dialog opens and scanning is enabled
	useEffect(() => {
		if (state.openDialog && state.isBarcodeScanningEnabled) {
			setTimeout(() => {
				const barcodeInput = document.getElementById('barCode-input');
				if (barcodeInput) {
					(barcodeInput as HTMLInputElement).focus();
				}
			}, 100);
		}
	}, [state.openDialog, state.isBarcodeScanningEnabled]);

	// Auto-enable barcode scanner when dialog opens
	useEffect(() => {
		if (state.openDialog) {
			dispatchState({ type: 'SET_BARCODE_SCANNING_ENABLED', payload: true });
		}
	}, [state.openDialog]);

	const handleAddItem = () => {
		if (!state.transactionType) {
			setOpenSnackBar(true);
			setSnackBarMsg('Please select a transaction type first');
			return;
		}
		dispatchState({ type: 'RESET_DIALOG_FORM' });
		dispatchState({ type: 'SET_OPEN_DIALOG', payload: true });
	};

	const resetDialogForm = () => {
		dispatchState({ type: 'RESET_DIALOG_FORM' });
	};

	const handleConfirmAddItem = (keepOpen: boolean = false) => {
		// Validate required fields
		if (!state.selectedMenuItemID) {
			setOpenSnackBar(true);
			setSnackBarMsg('Please select a stock item.');
			return;
		}

		// Validate quantity
		if (state.qty === 0 || isNaN(state.qty)) {
			setOpenSnackBar(true);
			setSnackBarMsg('Quantity must not be zero.');
			return;
		}

		// Validate stock out quantity
		if (state.transactionType === 'STOCK_OUT' && state.qty > state.balanceQty) {
			setOpenSnackBar(true);
			setSnackBarMsg(
				`Insufficient stock. Available: ${state.balanceQty.toFixed(4)}`,
			);
			return;
		}

		// Validate reason for STOCK_OUT and STOCK_ADJUSTMENT
		if (
			(state.transactionType === 'STOCK_OUT' ||
				state.transactionType === 'STOCK_ADJUSTMENT') &&
			!state.reasonID
		) {
			setOpenSnackBar(true);
			setSnackBarMsg('Please select a reason.');
			return;
		}

		const selectedMenuItem = getMenuItem?.find(
			(item: any) => item.ID === state.selectedMenuItemID,
		);
		const selectedUom = state.selectedUomID
			? getUOM?.find((uom: any) => uom.ID === state.selectedUomID)
			: null;
		const selectedReason = state.reasonID
			? getReason?.find((reason: any) => reason.ID === state.reasonID)
			: null;

		const newItem: StockTransactionItem = {
			ID:
				state.selectedItemIndex !== null &&
				state.items[state.selectedItemIndex]?.ID
					? state.items[state.selectedItemIndex].ID
					: undefined,
			menuItemID: state.selectedMenuItemID,
			menuItemName: selectedMenuItem?.name || '',
			uomID: state.selectedUomID || '',
			uomName: selectedUom?.name || '',
			reasonID: state.reasonID || undefined,
			reasonName: selectedReason?.name || undefined,
			remark: state.remark || '',
		};

		// Set quantity based on transaction type
		if (state.transactionType === 'STOCK_IN') {
			newItem.qtyReceived = state.qty;
		} else if (state.transactionType === 'STOCK_OUT') {
			newItem.transferQty = state.qty;
		} else if (state.transactionType === 'STOCK_ADJUSTMENT') {
			newItem.qtyAdjusted = state.qty;
		}

		if (state.selectedItemIndex !== null) {
			const updatedItems = [...state.items];
			updatedItems[state.selectedItemIndex] = newItem;
			dispatchState({ type: 'SET_ITEMS', payload: updatedItems });
		} else {
			// If same item (menuItemID + uomID) already exists, merge quantity into lump sum
			const existingIndex = state.items.findIndex(
				i => i.menuItemID === newItem.menuItemID && i.uomID === newItem.uomID,
			);
			if (existingIndex >= 0) {
				const existing = state.items[existingIndex];
				const existingQty = getItemQty(existing, state.transactionType);
				const mergedItem = { ...newItem };
				if (state.transactionType === 'STOCK_IN') {
					mergedItem.qtyReceived = existingQty + state.qty;
				} else if (state.transactionType === 'STOCK_OUT') {
					mergedItem.transferQty = existingQty + state.qty;
				} else if (state.transactionType === 'STOCK_ADJUSTMENT') {
					mergedItem.qtyAdjusted = existingQty + state.qty;
				}
				if (existing.ID) mergedItem.ID = existing.ID;
				const updatedItems = [...state.items];
				updatedItems[existingIndex] = mergedItem;
				dispatchState({ type: 'SET_ITEMS', payload: updatedItems });
			} else {
				dispatchState({ type: 'ADD_ITEM', payload: newItem });
			}
		}

		if (keepOpen) {
			dispatchState({ type: 'RESET_DIALOG_FORM' });
		} else {
			dispatchState({ type: 'SET_OPEN_DIALOG', payload: false });
			dispatchState({ type: 'RESET_DIALOG_FORM' });
		}
	};

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		index: number,
	) => {
		dispatchState({ type: 'SET_ANCHOR_EL', payload: event.currentTarget });
		dispatchState({ type: 'SET_SELECTED_ITEM_INDEX', payload: index });
	};

	const handleMenuClose = () => {
		dispatchState({ type: 'SET_ANCHOR_EL', payload: null });
		dispatchState({ type: 'SET_SELECTED_ITEM_INDEX', payload: null });
	};

	const handleEditItem = (index: number) => {
		const item = state.items[index];
		dispatchState({ type: 'SET_EDIT_ITEM', payload: { item, index } });
	};

	const handleDeleteItem = (index: number) => {
		dispatchState({ type: 'REMOVE_ITEM', payload: index });
		handleMenuClose();
	};

	const handleSave = async () => {
		if (!state.transactionType) {
			setOpenSnackBar(true);
			setSnackBarMsg('Please select a transaction type');
			return;
		}

		if (!state.trxDate || state.items.length === 0) {
			setOpenSnackBar(true);
			setSnackBarMsg(
				'Please fill in all required fields and add at least one item',
			);
			return;
		}

		const input: any = {
			transactionType: state.transactionType,
			outletID: outletID,
			trxDate: state.trxDate,
			docRef: state.docRef || undefined,
			description: state.description || undefined,
			items: state.items.map(item => {
				const itemInput: any = {
					menuItemID: item.menuItemID,
					uomID: item.uomID || undefined,
					remark: item.remark || undefined,
				};

				if (state.transactionType === 'STOCK_IN') {
					itemInput.qtyReceived = item.qtyReceived;
				} else if (state.transactionType === 'STOCK_OUT') {
					itemInput.transferQty = item.transferQty;
					itemInput.reasonID = item.reasonID || undefined;
				} else if (state.transactionType === 'STOCK_ADJUSTMENT') {
					itemInput.qtyAdjusted = item.qtyAdjusted;
					itemInput.reasonID = item.reasonID || undefined;
				}

				return itemInput;
			}),
		};

		// Add conditional fields
		if (state.transactionType === 'STOCK_IN') {
			input.docNo = state.docNo || undefined;
		}

		if (state.transactionType === 'STOCK_OUT') {
			input.toWarehouseID = state.toWarehouseID || undefined;
		}

		try {
			await createStockTransaction({ variables: { input } });
		} catch (error) {
			console.error('Error saving Stock Transaction:', error);
		}
	};

	const handleCancel = () => {
		history.push(`/menu/outlet-app/${outletID}/stock-monitoring?tab=1`);
	};

	// Disable past dates
	const shouldDisableDate = (date: Date | null) => {
		if (state.transactionType === 'STOCK_ADJUSTMENT') {
			return false; // allow all dates, including backdate
		}
		if (!date) return false;
		const today = startOfDay(new Date());
		return isBefore(date, today);
	};

	const getQuantityLabel = () => {
		switch (state.transactionType) {
			case 'STOCK_IN':
				return 'Qty Received';
			case 'STOCK_OUT':
				return 'Transfer Qty';
			case 'STOCK_ADJUSTMENT':
				return 'Qty Adjusted';
			default:
				return 'Quantity';
		}
	};

	const getItemDisplayQty = (item: StockTransactionItem) => {
		if (state.transactionType === 'STOCK_IN') return item.qtyReceived || 0;
		if (state.transactionType === 'STOCK_OUT') return item.transferQty || 0;
		if (state.transactionType === 'STOCK_ADJUSTMENT')
			return item.qtyAdjusted || 0;
		return 0;
	};

	const isLoading =
		outletLoading ||
		menuItemLoading ||
		uomLoading ||
		reasonLoading ||
		createLoading ||
		stockBalanceLoading;

	return (
		<>
			{isLoading && <Loading />}
			<MainHeader
				onClick={handleCancel}
				mainBtn="back"
				smTitle="Outlet App"
				title={getOutlet[0]?.name || 'Loading...'}
				routeSegments={[
					{
						name: 'Stock Monitoring',
						path: `/menu/outlet-app/${outletID}/stock-monitoring`,
					},
					{ name: 'Stock Transaction', current: true },
				]}
			/>

			<ContentWrapper float footer>
				<Grid container spacing={2}>
					{/* Transaction Details Section */}
					<Grid item xs={12}>
						<Paper style={{ padding: '16px' }}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<TextField
										select
										label="Transaction Type"
										fullWidth
										required
										value={state.transactionType}
										onChange={e =>
											dispatchState({
												type: 'SET_TRANSACTION_TYPE',
												payload: e.target.value as StockTransactionType,
											})
										}
										InputProps={{
											style: { fontSize: '14px' },
										}}
									>
										{/* <MuiMenuItem value="">Select Type</MuiMenuItem> */}
										<MuiMenuItem value="STOCK_IN">Stock In</MuiMenuItem>
										<MuiMenuItem value="STOCK_OUT">Stock Out</MuiMenuItem>
										<MuiMenuItem value="STOCK_ADJUSTMENT">
											Stock Adjustment
										</MuiMenuItem>
									</TextField>
								</Grid>
								<Grid item xs={12} sm={6}>
									<KeyboardDatePicker
										label="Transaction Date"
										format="dd/MM/yyyy"
										value={state.trxDate}
										onChange={date =>
											dispatchState({ type: 'SET_TRX_DATE', payload: date })
										}
										shouldDisableDate={shouldDisableDate}
										fullWidth
										required
										InputProps={{
											style: { fontSize: '14px' },
										}}
									/>
								</Grid>
								{state.transactionType === 'STOCK_IN' && (
									<Grid item xs={12} sm={6}>
										<TextField
											label="Doc No"
											fullWidth
											value={state.docNo}
											onChange={e =>
												dispatchState({
													type: 'SET_DOC_NO',
													payload: e.target.value,
												})
											}
											InputProps={{
												style: { fontSize: '14px' },
											}}
										/>
									</Grid>
								)}
								{state.transactionType === 'STOCK_OUT' && (
									<Grid item xs={12} sm={6}>
										<Autocomplete
											options={warehouses}
											getOptionLabel={(option: any) => option?.name || ''}
											value={
												warehouses.find(
													(w: any) => w.ID === state.toWarehouseID,
												) || null
											}
											onChange={(_, newValue: any) => {
												dispatchState({
													type: 'SET_TO_WAREHOUSE_ID',
													payload: newValue?.ID || '',
												});
											}}
											renderInput={params => (
												<TextField
													{...params}
													label="To Warehouse"
													fullWidth
													InputProps={{
														style: { fontSize: '14px' },
														...params.InputProps,
													}}
												/>
											)}
										/>
									</Grid>
								)}
								<Grid item xs={12} sm={6}>
									<TextField
										label="Document Ref."
										fullWidth
										value={state.docRef}
										onChange={e =>
											dispatchState({
												type: 'SET_DOC_REF',
												payload: e.target.value,
											})
										}
										InputProps={{
											style: { fontSize: '14px' },
										}}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										label="Description"
										fullWidth
										value={state.description}
										onChange={e =>
											dispatchState({
												type: 'SET_DESCRIPTION',
												payload: e.target.value,
											})
										}
										multiline
										rows={2}
										InputProps={{
											style: { fontSize: '14px' },
										}}
									/>
								</Grid>
							</Grid>
						</Paper>
					</Grid>

					{/* Transaction Detail(s) Section */}
					<Grid item xs={12}>
						<CardContents
							devFullWidth={true}
							section={{
								header: {
									title: 'Transaction Detail(s)',
									icon: (
										<AddCircle
											onClick={e => {
												handleAddItem();
											}}
											style={{ color: '#ff9800' }}
										/>
									),
									onClickAction: handleAddItem,
								},
							}}
						>
							{state.items.length === 0 ? (
								<EmptyList
									title="No Record Found"
									subtitle="Add New Record now."
								/>
							) : (
								<List
									className="core-list"
									style={{ padding: 0, width: '100%' }}
								>
									{state.items.map((item, index) => (
										<React.Fragment
											key={`transaction-item-${index}-${item.menuItemID}`}
										>
											<ListItem
												style={{
													width: '100%',
													padding: '12px 16px',
													boxShadow: 'none',
												}}
											>
												<ListItemText
													primary={
														<div
															style={{
																display: 'flex',
																alignItems: 'flex-start',
															}}
														>
															<span
																style={{
																	fontWeight: 'bold',
																	marginRight: '8px',
																}}
															>
																{index + 1}.
															</span>
															<span
																className="xsTitle"
																style={{ fontWeight: 'bold' }}
															>
																{item.menuItemName || 'N/A'}
															</span>
														</div>
													}
													secondary={
														<>
															<div
																className="desc"
																style={{ marginTop: '4px', marginLeft: '20px' }}
															>
																{getQuantityLabel()}:{' '}
																{getItemDisplayQty(item).toFixed(4)}{' '}
																{item.uomName || ''}
															</div>
															{item.reasonName && (
																<div
																	className="desc"
																	style={{
																		marginTop: '4px',
																		marginLeft: '20px',
																	}}
																>
																	Reason: {item.reasonName}
																</div>
															)}
															{item.remark && (
																<div
																	className="desc"
																	style={{
																		marginTop: '4px',
																		marginLeft: '20px',
																		fontStyle: 'italic',
																	}}
																>
																	{item.remark}
																</div>
															)}
															{state.transactionType === 'STOCK_ADJUSTMENT' && (
																<div
																	className="flex-space"
																	style={{
																		marginTop: '4px',
																		marginLeft: '20px',
																	}}
																>
																	<span
																		className={`xsTitle right-info ${
																			getItemDisplayQty(item) >= 0
																				? 'color-primary-orange'
																				: 'color-error'
																		}`}
																		style={{ fontWeight: 'bold' }}
																	>
																		{getItemDisplayQty(item) >= 0 ? '+' : ''}
																		{getItemDisplayQty(item).toFixed(4)}{' '}
																		{item.uomName || ''}
																	</span>
																</div>
															)}
														</>
													}
												/>
												<ListItemSecondaryAction>
													<IconButton
														edge="end"
														aria-label="more"
														aria-controls={`menu-${index}`}
														aria-haspopup="true"
														onClick={e => handleMenuClick(e, index)}
													>
														<MoreVert />
													</IconButton>
												</ListItemSecondaryAction>
											</ListItem>
											{state.items.length > 1 &&
												index !== state.items.length - 1 && (
													<Divider
														variant="middle"
														style={{
															margin: '0 auto',
															border: 'none',
															borderTop: '1px solid #e0e0e0',
														}}
													/>
												)}
										</React.Fragment>
									))}
								</List>
							)}
						</CardContents>
					</Grid>
				</Grid>
			</ContentWrapper>
			<DynamicFooter
				options={[
					{
						name: 'Cancel',
						onClick: handleCancel,
						color: 'inherit',
					},
					{
						name: 'Save',
						onClick: handleSave,
						color: state.items.length > 0 ? 'primary' : 'inherit',
						disabled:
							state.items.length === 0 ||
							!state.transactionType ||
							!state.trxDate ||
							isLoading,
					},
				]}
			/>

			{/* Add Item Selection Dialog */}
			<CommonDialog
				fullWidth={true}
				open={state.openDialog}
				onClose={() => {
					resetDialogForm();
					dispatchState({ type: 'SET_OPEN_DIALOG', payload: false });
				}}
				sections={{
					header: {
						dynamic: (
							<div className="">
								<div className="dialog-dynamic-content">
									<div
										className="session"
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<div className="title" style={{ color: '#ff9800' }}>
											Transaction Detail
										</div>
										<div
											className="title"
											style={{ color: '#ff9800', fontSize: '14px' }}
										>
											{state.selectedItemIndex !== null ? 'Edit' : 'New'}
										</div>
									</div>
								</div>
							</div>
						),
					},
					body: () => (
						<>
							{/* Hidden input for barcode scanner */}
							<input
								type="text"
								id="barCode-input"
								name="barCode"
								style={{
									position: 'absolute',
									left: '-9999px',
									width: '1px',
									height: '1px',
									opacity: 0,
									pointerEvents: state.isBarcodeScanningEnabled
										? 'auto'
										: 'none',
								}}
								autoFocus={state.isBarcodeScanningEnabled}
								tabIndex={-1}
							/>

							<Autocomplete
								options={getMenuItem || []}
								getOptionLabel={(option: any) => option?.name || ''}
								value={
									getMenuItem?.find(
										(item: any) => item.ID === state.selectedMenuItemID,
									) || null
								}
								onChange={(_, newValue: any) => {
									const menuItemID = newValue?.ID || '';

									dispatchState({
										type: 'SET_MENU_ITEM_ERROR',
										payload: '',
									});
									dispatchState({
										type: 'SET_SELECTED_MENU_ITEM_ID',
										payload: menuItemID,
									});
									if (newValue?.stockUomID) {
										dispatchState({
											type: 'SET_SELECTED_UOM_ID',
											payload: newValue.stockUomID,
										});
									} else {
										dispatchState({ type: 'SET_SELECTED_UOM_ID', payload: '' });
									}
								}}
								renderInput={params => (
									<TextField
										{...params}
										label="Stock Item"
										margin="normal"
										required
										error={!!state.menuItemError}
											helperText={
											state.menuItemError ||
											(state.isBarcodeScanningEnabled
												? 'Barcode scanning active - Scan a barcode to auto-select item'
												: '')
										}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{params.InputProps.endAdornment}
													<InputAdornment position="end">
														<IconButton
															size="small"
															onClick={() => {
																dispatchState({
																	type: 'SET_BARCODE_SCANNING_ENABLED',
																	payload: !state.isBarcodeScanningEnabled,
																});
																if (!state.isBarcodeScanningEnabled) {
																	setTimeout(() => {
																		const barcodeInput = document.getElementById(
																			'barCode-input',
																		);
																		if (barcodeInput) {
																			barcodeInput.focus();
																		}
																	}, 100);
																}
															}}
															style={{
																color: state.isBarcodeScanningEnabled
																	? '#ff9800'
																	: 'inherit',
																backgroundColor: state.isBarcodeScanningEnabled
																	? 'rgba(255, 152, 0, 0.1)'
																	: 'transparent',
															}}
															title={
																state.isBarcodeScanningEnabled
																	? 'Barcode scanning active - Click to disable'
																	: 'Click to enable barcode scanning'
															}
														>
															<CropFreeIcon />
														</IconButton>
													</InputAdornment>
												</>
											),
										}}
									/>
								)}
								fullWidth
							/>

							<TextField
								label="UOM"
								select
								fullWidth
								margin="normal"
								value={state.selectedUomID}
								required
								disabled
							>
								<MuiMenuItem value="">
									<em>None</em>
								</MuiMenuItem>
								{getUOM?.map((uom: any) => (
									<MuiMenuItem key={uom.ID} value={uom.ID}>
										{uom.name}
									</MuiMenuItem>
								))}
							</TextField>

							{state.transactionType === 'STOCK_OUT' && (
								<TextField
									label="Balance Quantity"
									fullWidth
									margin="normal"
									value={state.balanceQty.toFixed(4)}
									InputProps={{
										readOnly: true,
										style: { fontSize: '14px' },
									}}
									helperText="Current stock balance"
								/>
							)}

							<NumberFormat
								customInput={TextField}
								label={getQuantityLabel()}
								fullWidth
								margin="normal"
								value={state.qty || ''}
								onValueChange={values => {
									const qty = values.floatValue || 0;
									dispatchState({ type: 'SET_QTY', payload: qty });
								}}
								required
								decimalScale={4}
								fixedDecimalScale={false}
								allowNegative={state.transactionType === 'STOCK_ADJUSTMENT'}
								thousandSeparator={false}
								error={state.qty === 0 && state.selectedMenuItemID !== ''}
								helperText={
									state.qty === 0 && state.selectedMenuItemID !== ''
										? 'Please fill in the quantity'
										: state.transactionType === 'STOCK_ADJUSTMENT'
										? 'Enter positive value to increase, negative to decrease'
										: state.transactionType === 'STOCK_OUT' &&
										  state.qty > state.balanceQty
										? `Insufficient stock. Available: ${state.balanceQty.toFixed(
												4,
										  )}`
										: ''
								}
							/>

							{(state.transactionType === 'STOCK_OUT' ||
								state.transactionType === 'STOCK_ADJUSTMENT') && (
								<Autocomplete
									options={getReason || []}
									getOptionLabel={(option: any) => option?.name || ''}
									value={
										getReason?.find(
											(reason: any) => reason.ID === state.reasonID,
										) || null
									}
									onChange={(_, newValue: any) => {
										dispatchState({
											type: 'SET_REASON_ID',
											payload: newValue?.ID || '',
										});
									}}
									renderInput={params => (
										<TextField
											{...params}
											label="Reason"
											margin="normal"
											required
											InputProps={{
												style: { fontSize: '14px' },
												...params.InputProps,
											}}
										/>
									)}
									fullWidth
								/>
							)}

							<TextField
								label="Remark"
								fullWidth
								margin="normal"
								multiline
								rows={2}
								value={state.remark}
								onChange={e =>
									dispatchState({ type: 'SET_REMARK', payload: e.target.value })
								}
								// InputProps={{
								// 	endAdornment: (
								// 		<InputAdornment position="end">
								// 			<IconButton size="small" disabled>
								// 				<MicIcon />
								// 			</IconButton>
								// 		</InputAdornment>
								// 	),
								// }}
							/>
						</>
					),
					footer: {
						actions: [
							{
								displayText: 'Close',
								props: {
									onClick: () => {
										resetDialogForm();
										dispatchState({ type: 'SET_OPEN_DIALOG', payload: false });
									},
									variant: 'outlined',
									style: {
										borderColor: '#ff9800',
										color: '#ff9800',
										backgroundColor: 'white',
									},
								},
							},
							{
								displayText: 'Confirm & New',
								props: {
									onClick: () => handleConfirmAddItem(true),
									variant: 'contained',
									style: {
										backgroundColor: '#ff9800',
										color: 'white',
									},
									disabled:
										!state.selectedMenuItemID ||
										state.qty === 0 ||
										(state.transactionType === 'STOCK_OUT' &&
											state.qty > state.balanceQty) ||
										((state.transactionType === 'STOCK_OUT' ||
											state.transactionType === 'STOCK_ADJUSTMENT') &&
											!state.reasonID),
								},
							},
							{
								displayText: 'Confirm',
								props: {
									onClick: () => handleConfirmAddItem(false),
									variant: 'contained',
									style: {
										backgroundColor: '#ff9800',
										color: 'white',
									},
									disabled:
										!state.selectedMenuItemID ||
										!!state.menuItemError ||
										state.qty === 0 ||
										(state.transactionType === 'STOCK_OUT' &&
											state.qty > state.balanceQty) ||
										((state.transactionType === 'STOCK_OUT' ||
											state.transactionType === 'STOCK_ADJUSTMENT') &&
											!state.reasonID),
								},
							},
						],
					},
				}}
			/>

			{/* Menu for item actions */}
			<Menu
				id="item-menu"
				anchorEl={state.anchorEl}
				keepMounted
				open={Boolean(state.anchorEl)}
				onClose={handleMenuClose}
			>
				<MuiMenuItem
					onClick={() => {
						if (state.selectedItemIndex !== null) {
							handleEditItem(state.selectedItemIndex);
						}
					}}
				>
					Edit
				</MuiMenuItem>
				<MuiMenuItem
					onClick={() => {
						if (state.selectedItemIndex !== null) {
							handleDeleteItem(state.selectedItemIndex);
						}
					}}
				>
					Delete
				</MuiMenuItem>
			</Menu>
		</>
	);
};
